import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { nacebelCodeReducer } from "../../../state/reducers";
import { INacebelCode } from "../../../utils/types/models";

const NacebelCodeEditor: React.FC<IAddEntityScreenProps<INacebelCode> | IEditEntityScreenProps<INacebelCode>> = (props: IAddEntityScreenProps<INacebelCode> | IEditEntityScreenProps<INacebelCode>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(nacebelCodeReducer, getInitialState<INacebelCode>());
	const intialNacebelCode: INacebelCode = {
		id: (props as IEditEntityScreenProps<INacebelCode>).recordId || 0,
		name: "",
		active: true
	};
	const [nacebelCode, setNacebelCode] = useState<INacebelCode>(intialNacebelCode);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setNacebelCode(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setNacebelCode(handleChange(nacebelCode, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(nacebelCode.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: INacebelCode): void {
		if (record && !close) {
			setNacebelCode(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<INacebelCode>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={nacebelCode}
			endpoint={Endpoint.NacebelCodes}
			entityState={state}
			entityType={t("nacebelCode")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={nacebelCode.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={nacebelCode.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={nacebelCode.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default NacebelCodeEditor;
